import React, { useEffect, useRef } from 'react';

interface Configuration {
    name: string;
    creditsForPostingLoad: number;
    creditsForAnsweringRequest: number;
    creditsForInitialSignup: number;
    creditsForFirstFiveAnswers: number;
    description: string;
    creditsForReferrals: number;
    isActive: boolean;
}

interface AddConfigurationModalProps {
    newCredit: Configuration;
    setNewCredit: (value: Configuration) => void;
    onAdd: (e: React.FormEvent) => void;
    onClose: () => void;
    isSubmitting?: boolean;
}

const AddCreditModal: React.FC<AddConfigurationModalProps> = ({
    newCredit,
    setNewCredit,
    onAdd,
    onClose,
    isSubmitting = false,
}) => {
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [creditErrors, setCreditErrors] = React.useState<Record<string, string>>({});

    useEffect(() => {
        if (nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={onClose}
        >
            <div
                className="relative p-4 w-full max-w-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    tabIndex={-1}
                    role="dialog"
                    aria-labelledby="modal-title"
                    className="relative bg-white rounded-lg shadow pb-4"
                >
                    <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
                        <p id="modal-title" className="text-base font-semibold text-grease-100">
                            Add New Configuration
                        </p>
                        <button
                            type="button"
                            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                            onClick={onClose}
                        >
                            <svg
                                className="w-3 h-3"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 14 14"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                                />
                            </svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>

                    <form onSubmit={onAdd}>
                        <div className="px-4 py-4">
                            <div>
                                <label htmlFor="config-name" className="text-xs font-light text-gray-500">
                                    Configuration Name
                                <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    id="config-name"
                                    type="text"
                                    value={newCredit.name}
                                    onChange={(e) => setNewCredit({ ...newCredit, name: e.target.value })}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                    ref={nameInputRef}
                                    required
                                />
                            </div>

                            <div className="mt-2">
                                <label htmlFor="credits-posting-load" className="text-xs font-light text-gray-500 ">
                                    Tokens for Posting a Load
                                </label>
                                <input
                                    id="credits-posting-load"
                                    type="number"
                                    value={newCredit.creditsForPostingLoad}
                                    onChange={(e) => {
                                        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
                                        if (value > 100) {
                                            setCreditErrors({...creditErrors, postingLoad: 'Credit value cannot exceed 100'});
                                            return;
                                        }
                                        setCreditErrors({...creditErrors, postingLoad: ''});
                                        setNewCredit({
                                            ...newCredit,
                                            creditsForPostingLoad: value,
                                        });
                                    }}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                    min="0"
                                    max="100"
                                />
                                {creditErrors.postingLoad && <p className="text-red-500 text-xs mt-1">{creditErrors.postingLoad}</p>}
                            </div>

                            <div className="mt-2">
                                <label htmlFor="credits-answering-request" className="text-xs font-light text-gray-500">
                                    Tokens for Answering a Request
                                </label>
                                <input
                                    id="credits-answering-request"
                                    type="number"
                                    value={newCredit.creditsForAnsweringRequest}
                                    onChange={(e) => {
                                        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
                                        if (value > 100) {
                                            setCreditErrors({...creditErrors, answeringRequest: 'Credit value cannot exceed 100'});
                                            return;
                                        }
                                        setCreditErrors({...creditErrors, answeringRequest: ''});
                                        setNewCredit({
                                            ...newCredit,
                                            creditsForAnsweringRequest: value,
                                        });
                                    }}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                    min="0"
                                    max="100"
                                />
                                {creditErrors.answeringRequest && <p className="text-red-500 text-xs mt-1">{creditErrors.answeringRequest}</p>}
                            </div>

                            {/* <div className="mt-2">
                                <label htmlFor="credits-initial-signup" className="text-xs font-light text-gray-500">
                                    Credits for Initial Signup
                                </label>
                                <input
                                    id="credits-initial-signup"
                                    type="number"
                                    value={newCredit.creditsForInitialSignup}
                                    onChange={(e) => {
                                        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
                                        if (value > 100) {
                                            setCreditErrors({...creditErrors, initialSignup: 'Credit value cannot exceed 100'});
                                            return;
                                        }
                                        setCreditErrors({...creditErrors, initialSignup: ''});
                                        setNewCredit({
                                            ...newCredit,
                                            creditsForInitialSignup: value,
                                        });
                                    }}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2"
                                    min="0"
                                    max="100"
                                />
                                {creditErrors.initialSignup && <p className="text-red-500 text-xs mt-1">{creditErrors.initialSignup}</p>}
                            </div> */}

                            <div className="mt-2">
                                <label htmlFor="credits-first-five-answers" className="text-xs font-light text-gray-500">
                                    Tokens for the First 5 Answers
                                </label>
                                <input
                                    id="credits-first-five-answers"
                                    type="number"
                                    value={newCredit.creditsForFirstFiveAnswers}
                                    onChange={(e) => {
                                        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
                                        if (value > 100) {
                                            setCreditErrors({...creditErrors, firstFiveAnswers: 'Credit value cannot exceed 100'});
                                            return;
                                        }
                                        setCreditErrors({...creditErrors, firstFiveAnswers: ''});
                                        setNewCredit({
                                            ...newCredit,
                                            creditsForFirstFiveAnswers: value,
                                        });
                                    }}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                    min="0"
                                    max="100"
                                />
                                {creditErrors.firstFiveAnswers && <p className="text-red-500 text-xs mt-1">{creditErrors.firstFiveAnswers}</p>}
                            </div>

                            <div className="mt-2">
                                <label htmlFor="credits-referrals" className="text-xs font-light text-gray-500">
                                    Tokens for Referrals
                                </label>
                                <input
                                    id="credits-referrals"
                                    type="number"
                                    value={newCredit.creditsForReferrals}
                                    onChange={(e) => {
                                        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
                                        if (value > 100) {
                                            setCreditErrors({...creditErrors, referrals: 'Credit value cannot exceed 100'});
                                            return;
                                        }
                                        setCreditErrors({...creditErrors, referrals: ''});
                                        setNewCredit({
                                            ...newCredit,
                                            creditsForReferrals: value,
                                        });
                                    }}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                    min="0"
                                    max="100"
                                />
                                {creditErrors.referrals && <p className="text-red-500 text-xs mt-1">{creditErrors.referrals}</p>}
                            </div>

                            <div className="mt-4">
                                <label htmlFor="status" className="text-xs font-light text-gray-500">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    value={String(newCredit.isActive)}
                                    onChange={(e) =>
                                        setNewCredit({
                                            ...newCredit,
                                            isActive: e.target.value === 'true',
                                        })
                                    }
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 border-none"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Disable</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-4 items-center justify-end px-4 mt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex items-center gap-2 py-3 px-6 bg-gray-300 bg-opacity-20 rounded-md"
                            >
                                <p className="font-medium text-gray-500 text-xs">Cancel</p>
                            </button>
                            <button
                                type="submit"
                                className={`flex items-center gap-2 py-3 px-6 rounded-md text-white text-xs ${
                                    isSubmitting ? 'bg-[#FFD700] opacity-70 cursor-not-allowed' : 'bg-[#FFD700]'
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="w-4 h-4 animate-spin"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <span>Submit</span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddCreditModal;